import { BaseSelect, SelectProps } from '@app/components';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PaymentProvider } from '@app/common';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

const columnHelper = createColumnHelper<PaymentProvider>();

const getPaymentProviderLabel = (provider: PaymentProvider): string => {
  switch (provider) {
    case PaymentProvider.PAYGREEN:
      return 'Paygreen';
    case PaymentProvider.WORLDLINE:
      return 'Worldline';
    case PaymentProvider.PAYLINE:
      return 'Payline';
    default:
      return provider;
  }
};

// Only show active providers (exclude deprecated PAYLINE)
const availableProviders = [PaymentProvider.PAYGREEN, PaymentProvider.WORLDLINE];

interface PaymentProviderTypeSelectProps extends Omit<SelectProps<any>, 'table'> {
  disabled?: boolean;
}

export const PaymentProviderTypeSelect = ({ 
  label = 'Prestataire de paiement', 
  name, 
  disabled = false,
  ...props 
}: PaymentProviderTypeSelectProps) => {
  const { control, setValue } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  
  const value = useWatch({
    control,
    name: name!,
    defaultValue: PaymentProvider.PAYGREEN
  });

  const selectedValues: PaymentProvider[] = value ? [value] : [];

  const columns = [
    columnHelper.display({
      id: 'provider-type',
      header: () => (
        <BaseSelect.ListHeaderItem width="100%">
          Nom
        </BaseSelect.ListHeaderItem>
      ),
      cell: (info) => {
        return <BaseSelect.ListItemCol width="100%">
          <BaseSelect.ListItemText numberOfLines={1} bold>
            {getPaymentProviderLabel(info.row.original)}
          </BaseSelect.ListItemText>
        </BaseSelect.ListItemCol>
      },
    }),
  ];

  const table = useReactTable({
    data: availableProviders,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const onSelect = (item: PaymentProvider) => {
    if (disabled) return;
    setValue(name!, item, { shouldDirty: true });
    setIsOpen(false);
  };

  const onRemove = (_item: PaymentProvider) => {
    // Don't allow removing for single select
  };

  const isActive = (item: PaymentProvider) => selectedValues.includes(item);

  const formattedLabel = () => {
    if (!selectedValues.length) {
      return label!;
    }
    
    return label + ': ' + getPaymentProviderLabel(selectedValues[0]);
  };

  return (
    <BaseSelect
      name={name}
      label={label}
      table={table}
      isOpen={isOpen}
      setIsOpen={(open) => !disabled && setIsOpen(open)}
      values={selectedValues}
      onSelect={onSelect}
      onRemove={onRemove}
      isActive={isActive}
      formattedLabel={formattedLabel()}
      render={(item) => (
        <BaseSelect.ListItem key={item as string} compact isActive onPress={() => {}}>
          <BaseSelect.ListItemCol width='100%'>
            <BaseSelect.ListItemText numberOfLines={1} bold>
              {getPaymentProviderLabel(item as PaymentProvider)}
            </BaseSelect.ListItemText>
          </BaseSelect.ListItemCol>
        </BaseSelect.ListItem>
      )}
      {...props} 
    />
  );
};



